import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RecipeCard from '../Recipes/RecipeCard';
import RecipeDrawer from '../Recipes/RecipeDrawer';
import MyMealsStickyFooter from '../Common/MyMealsStickyFooter';
import ReplaceMealPlanDrawer from '../MealPlan/ReplaceMealPlanDrawer';
import backendClient from '../../services/backendClient';
import { useFavoriteMeals } from '../../hooks/useFavoriteMeals';
import { useUser } from '../../contexts/UserContext';

// Meal-type icon (matches MyMealPlans.js styling).
const MealTypeIcon = ({ mealType, active }) => {
  const stroke = active ? '#0D0C07' : '#65635D';
  if (mealType === 'Breakfast') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M8.33333 1.6665V3.33317M11.6667 1.6665V3.33317M13.3333 6.6665C13.5543 6.6665 13.7663 6.7543 13.9226 6.91058C14.0789 7.06686 14.1667 7.27882 14.1667 7.49984V14.1665C14.1667 15.0506 13.8155 15.8984 13.1904 16.5235C12.5652 17.1486 11.7174 17.4998 10.8333 17.4998H5.83333C4.94928 17.4998 4.10143 17.1486 3.47631 16.5235C2.85119 15.8984 2.5 15.0506 2.5 14.1665V7.49984C2.5 7.27882 2.5878 7.06686 2.74408 6.91058C2.90036 6.7543 3.11232 6.6665 3.33333 6.6665H15C15.8841 6.6665 16.7319 7.01769 17.357 7.64281C17.9821 8.26794 18.3333 9.11578 18.3333 9.99984C18.3333 10.8839 17.9821 11.7317 17.357 12.3569C16.7319 12.982 15.8841 13.3332 15 13.3332H14.1667M5 1.6665V3.33317" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (mealType === 'Lunch') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M13.3333 1.65967L11.4166 3.57633C10.9586 4.04366 10.702 4.67195 10.702 5.32633C10.702 5.98072 10.9586 6.60901 11.4166 7.07633L12.9166 8.57634C13.384 9.0344 14.0123 9.29098 14.6666 9.29098C15.321 9.29098 15.9493 9.0344 16.4166 8.57634L18.3333 6.65967M12.5 12.493L2.74997 2.743C2.41737 3.06888 2.15315 3.45785 1.97276 3.88713C1.79238 4.31641 1.69946 4.77736 1.69946 5.243C1.69946 5.70864 1.79238 6.1696 1.97276 6.59888C2.15315 7.02815 2.41737 7.41712 2.74997 7.743L8.83331 13.8263C9.41664 14.4097 10.5 14.4097 11.1666 13.8263L12.5 12.493ZM12.5 12.493L18.3333 18.3263M1.74997 18.1597L7.08331 12.9097M15.8333 4.15967L9.99997 9.993" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (mealType === 'Dinner') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
        <path d="M10.0001 3.48617V2.65283M10.0001 3.48617C8.45299 3.48617 6.96925 4.10075 5.87529 5.19471C4.78133 6.28867 4.16675 7.7724 4.16675 9.3195M10.0001 3.48617C11.5472 3.48617 13.0309 4.10075 14.1249 5.19471C15.2188 6.28867 15.8334 7.7724 15.8334 9.3195M12.8334 15.4862L15.5001 13.1528C15.6588 12.9996 15.8461 12.8792 16.0513 12.7983C16.2565 12.7175 16.4757 12.6779 16.6962 12.6818C16.9168 12.6857 17.1344 12.7329 17.3366 12.8209C17.5389 12.9089 17.7219 13.0358 17.8751 13.1945C18.0283 13.3532 18.1487 13.5405 18.2296 13.7457C18.3104 13.9509 18.35 14.1701 18.3461 14.3906C18.3423 14.6112 18.295 14.8288 18.207 15.0311C18.119 15.2333 17.9921 15.4163 17.8334 15.5695L14.8334 18.3195C14.2501 18.9862 13.4167 19.3195 12.5001 19.3195H9.16675C8.25008 19.3195 7.41675 18.9862 6.83342 18.3195L5.74842 17.0995C5.6703 17.0115 5.57444 16.9411 5.46715 16.8928C5.35986 16.8446 5.24356 16.8196 5.12592 16.8195H4.16675M1.66675 12.6528H11.6667C12.1088 12.6528 12.5327 12.8284 12.8453 13.141C13.1578 13.4535 13.3334 13.8775 13.3334 14.3195C13.3334 14.7615 13.1578 15.1854 12.8453 15.498C12.5327 15.8106 12.1088 15.9862 11.6667 15.9862H10.0001M3.33341 9.3195H16.6667M4.16675 12.6528V17.6528C4.16675 17.8738 4.07895 18.0858 3.92267 18.2421C3.76639 18.3984 3.55443 18.4862 3.33341 18.4862H1.66675" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // "Other" — generic circle icon
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="2.25" stroke={stroke} strokeWidth="1.75" />
      <circle cx="10" cy="10" r="7.25" stroke={stroke} strokeWidth="1.75" />
    </svg>
  );
};

function FavoriteMeals() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'mealplan']);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeMealTab, setActiveMealTab] = useState(null);
  const [startOverConfirmDrawerOpen, setStartOverConfirmDrawerOpen] = useState(false);

  const { numberOfPeople, cookingStyle } = useUser();
  const {
    favoriteMeals,
    favoriteMealsCount,
    isLoadingFavorites,
    isMealFavorite,
    toggleFavoriteMeal,
  } = useFavoriteMeals();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const groupedFavorites = useMemo(() => {
    return {
      Breakfast: favoriteMeals.filter((meal) => meal.mealType === 'Breakfast'),
      Lunch: favoriteMeals.filter((meal) => meal.mealType === 'Lunch'),
      Dinner: favoriteMeals.filter((meal) => meal.mealType === 'Dinner'),
      Other: favoriteMeals.filter(
        (meal) => !['Breakfast', 'Lunch', 'Dinner'].includes(meal.mealType)
      ),
    };
  }, [favoriteMeals]);

  const availableTabs = useMemo(() => {
    return [
      ...(groupedFavorites.Breakfast.length > 0 ? ['Breakfast'] : []),
      ...(groupedFavorites.Lunch.length > 0 ? ['Lunch'] : []),
      ...(groupedFavorites.Dinner.length > 0 ? ['Dinner'] : []),
      ...(groupedFavorites.Other.length > 0 ? ['Other'] : []),
    ];
  }, [groupedFavorites]);

  // Initialize / keep active tab in sync with available tabs.
  useEffect(() => {
    if (availableTabs.length === 0) {
      if (activeMealTab !== null) setActiveMealTab(null);
      return;
    }
    if (!activeMealTab || !availableTabs.includes(activeMealTab)) {
      setActiveMealTab(availableTabs[0]);
    }
  }, [availableTabs, activeMealTab]);

  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
    setDrawerOpen(true);
  };

  const handleStartOver = async () => {
    try {
      await backendClient.delete('/api/mealplan');
    } catch (error) {
      console.error('Failed to delete meal plan:', error);
    }
    navigate('/wizard', { state: { fromStartOver: true } });
  };

  const getTabLabel = (mealType) =>
    mealType === 'Other'
      ? t('profileSettings.favoriteMealsOther', { ns: 'common' })
      : t(`mealTypes.${mealType.toLowerCase()}`, { ns: 'mealplan' });

  const renderMealCard = (recipe, index, fullWidth = false) => (
    <RecipeCard
      key={recipe.favoriteId || recipe.id || recipe.title || index}
      recipe={recipe}
      index={index}
      onRecipeClick={handleRecipeClick}
      imagesLoading={{}}
      isRecipeReady={true}
      isRecipeClickable={true}
      isSavedRecipe={true}
      showCookingActions={false}
      isFavorite={isMealFavorite(recipe)}
      onToggleFavorite={toggleFavoriteMeal}
      showFavoriteButton={true}
      fullWidth={fullWidth}
    />
  );

  // Desktop section: horizontal-scroll row of cards per meal type (same pattern as MyMealPlans).
  const renderDesktopSection = (mealType, meals) => {
    if (!meals || meals.length === 0) return null;
    return (
      <div key={mealType} className="max-w-5xl mx-auto">
        <div className="pt-3 flex mb-3 px-6 justify-between items-center">
          <h3 className="text-[#0D0C07] text-center text-[18px] font-bold leading-[32px] flex items-center">
            <div className="flex items-center pb-0.5 justify-center w-[20px] h-[20px]">
              <MealTypeIcon mealType={mealType} active={true} />
            </div>
            <span className="ml-1.5">{getTabLabel(mealType)}</span>
          </h3>
          <span className="text-[#878683] text-[12.8px] font-normal">
            {t('mealCount', { count: meals.length, ns: 'mealplan' })}
          </span>
        </div>

        <div className="max-w-5xl mx-auto px-6 pb-4">
          <div className="grid grid-cols-3 gap-4">
            {meals.map((recipe, index) => renderMealCard(recipe, index, false))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-safe-screen flex flex-col">
      {/* Yellow header area */}
      <div className="px-6 pt-7 pb-5 max-w-5xl mx-auto w-full">
        <h1 className="text-2xl font-bold mb-2">
          {t('profileSettings.favoriteMealsTitle', { ns: 'common' })}
        </h1>
        <p className="font-medium text-[#65635D] text-[14px] leading-[24px] mb-4">
          {t('profileSettings.favoriteMealsPersonalizes', { ns: 'common' })}
        </p>
        <div className="flex gap-2 mb-0">
          <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-[10px] border-[0.5px] border-[#F3F3F2] bg-[rgba(255,255,255,0.64)] shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)] backdrop-blur-lg">
            {t('profileSettings.favoriteMealsSavedTag', { ns: 'common', count: favoriteMealsCount })}
          </span>
        </div>
      </div>

      {/* Content panel — rounded top, elevated shadow (matches MyMealPlans) */}
      <div
        className="flex-1 min-h-0 mt-2 pt-0 md:pt-3 pb-32
                    bg-[#F8F8F7]
                    rounded-[12px_12px_0px_0px]
                    border-t-[2px]
                    border-t-[#FFF]
                    shadow-[0px_-4px_24px_0px_rgba(13,12,7,0.12)]"
      >
        {isLoadingFavorites ? (
          <div className="max-w-5xl mx-auto px-6 pt-6 text-[#65635D] text-[14px]">
            {t('buttons.loading', { ns: 'common' })}
          </div>
        ) : favoriteMealsCount === 0 ? (
          <div className="max-w-5xl mx-auto px-6 pt-6">
            <div className="rounded-[12px] bg-white border border-[#E2E1DF] p-5 text-[#56554D] text-[14px] leading-[22px]">
              {t('profileSettings.favoriteMealsEmpty', { ns: 'common' })}
            </div>
          </div>
        ) : (
          <>
            {/* Mobile tabs */}
            <div className="md:hidden px-6 pt-2 flex border-b border-[#E8E6E3]">
              {availableTabs.map((mealType) => (
                <div
                  key={mealType}
                  onClick={() => setActiveMealTab(mealType)}
                  className={`flex-1 py-3 text-center justify-center flex items-center border-b-[2px] cursor-pointer transition-colors ${
                    activeMealTab === mealType
                      ? 'border-[#FFCB00] text-[#0D0C07] font-bold'
                      : 'text-[#65635D] font-normal border-[transparent]'
                  }`}
                >
                  <span className="flex items-center justify-center w-[20px] h-[20px]">
                    <MealTypeIcon mealType={mealType} active={activeMealTab === mealType} />
                  </span>
                  <span className="ml-1.5 text-[14px] leading-[32px]">
                    {getTabLabel(mealType)}
                  </span>
                </div>
              ))}
            </div>

            {/* Mobile: sliding panels */}
            <div className="md:hidden overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{
                  transform: `translateX(-${Math.max(
                    0,
                    availableTabs.indexOf(activeMealTab)
                  ) * 100}%)`,
                }}
              >
                {availableTabs.map((mealType) => {
                  const meals = groupedFavorites[mealType] || [];
                  return (
                    <div
                      key={mealType}
                      className="w-full flex-shrink-0 px-6 pt-4"
                    >
                      <div className="flex flex-col gap-4 mt-3">
                        {meals.map((recipe, index) =>
                          renderMealCard(recipe, index, true)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop: horizontal-scroll rows per meal type */}
            <div className="hidden md:block">
              {renderDesktopSection('Breakfast', groupedFavorites.Breakfast)}
              {renderDesktopSection('Lunch', groupedFavorites.Lunch)}
              {renderDesktopSection('Dinner', groupedFavorites.Dinner)}
              {renderDesktopSection('Other', groupedFavorites.Other)}
            </div>
          </>
        )}
      </div>

      <RecipeDrawer
        selectedRecipe={selectedRecipe}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        imagesLoading={{}}
        onDislikeRecipe={() => {}}
        isGeneratingAlternatives={false}
        dislikedRecipe={null}
        numberOfPeople={numberOfPeople}
        cookingStyle={cookingStyle}
        groceryListLoaded={true}
        hideRemoveButton={true}
      />

      <MyMealsStickyFooter
        activeTab="favourites"
        isPremium={true}
        onStartNewMealPlan={() => setStartOverConfirmDrawerOpen(true)}
        onOpenMeals={() => navigate('/mymeals')}
        onOpenGroceries={() => navigate('/mymeals/groceries')}
      />

      {/* Start Over Confirmation Drawer */}
      <ReplaceMealPlanDrawer
        isOpen={startOverConfirmDrawerOpen}
        onClose={() => setStartOverConfirmDrawerOpen(false)}
        onConfirmReplace={() => {
          setStartOverConfirmDrawerOpen(false);
          handleStartOver();
        }}
        i18nPrefix="startOverMealPlan"
        isPremium={true}
      />
    </div>
  );
}

export default FavoriteMeals;
